import { isNil } from 'lodash';

import { ErrorCodes } from '@enums/error';
import { addClientTokenToLocalStorage, getAuthToken } from '@utils/auth';
import { getErrorMessage } from '@utils/error';

import { regenerateToken } from './regenerate-token';
import {
  Handle401Type,
  HttpClientError,
  RegenerateAndRetryRequestType,
  RequestType,
} from './types';

const request = async ({
  axiosInstance,
  requestPayload,
  token,
}: RequestType) => {
  try {
    const response = await axiosInstance.request({
      ...requestPayload,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return response.data;
  } catch (error: any) {
    throw new Error(error);
  }
};

const regenerateTokenAndRetryRequest = async ({
  requestPayload,
  clientData,
  axiosInstance,
}: RegenerateAndRetryRequestType) => {
  const { clientUuid } = clientData;
  const token = await regenerateToken({
    ...clientData,
    ignoreSingleClientToken: true,
    logoutOnError: true,
  });

  const newToken = token.accessToken;
  addClientTokenToLocalStorage({
    clientUuid: clientUuid!,
    clientToken: newToken,
  });

  const response = await request({
    requestPayload,
    axiosInstance,
    token: newToken,
  });

  return response;
};

const handle401Error = async ({
  mutex,
  httpClient,
  clientData,
}: Handle401Type) => {
  if (!mutex.isLocked()) {
    const release = await mutex.acquire();
    try {
      const response = await regenerateTokenAndRetryRequest({
        clientData,
        ...httpClient,
      });

      return response;
    } finally {
      release();
    }
  } else {
    await mutex.waitForUnlock();

    const { clientUuid } = clientData;
    const token = getAuthToken({ clientUuid });

    const response = await request({ token, ...httpClient });

    return response;
  }
};

export const handleError = async ({
  error,
  mutex,
  httpClient,
  clientData,
}: HttpClientError) => {
  try {
    const responseData = error?.response?.data as { code?: string };

    if (
      (error?.response?.status === 401 &&
        responseData?.code === ErrorCodes.BD_UNAUTHORIZED) ||
      responseData?.code === ErrorCodes.INACTIVE_TOKEN
    ) {
      const response = await handle401Error({ mutex, httpClient, clientData });

      return response;
    } else {
      const msg = isNil(error?.response)
        ? error.message
        : getErrorMessage(error);

      throw new Error(msg);
    }
  } catch (error: any) {
    throw new Error(error.message);
  }
};
